import type { CSSProperties } from "react";
import { z } from "zod";

import { chartHexColorSchema } from "@/lib/utils/chartUtils";

/** Named font stacks a saved report text style may choose; omit to inherit the theme font. */
export const REPORT_FONT_FAMILY_STACKS = {
  serif: 'Georgia, Cambria, "Times New Roman", Times, serif',
  sans: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  mono: 'ui-monospace, Menlo, Consolas, "Liberation Mono", monospace',
} as const;

export type ReportFontFamily = keyof typeof REPORT_FONT_FAMILY_STACKS;

const REPORT_FONT_FAMILIES = Object.keys(REPORT_FONT_FAMILY_STACKS) as [
  ReportFontFamily,
  ...ReportFontFamily[],
];

const REPORT_TEXT_ALIGNS = ["left", "center", "right"] as const;

const reportSpacingPxSchema = z.number().int().min(0).max(64);

/** Bounded typography for one report text surface (title, label, headline, or body). */
export const reportTextStyleSchema = z
  .object({
    fontFamily: z.enum(REPORT_FONT_FAMILIES).optional(),
    fontSizePx: z.number().int().min(10).max(64).optional(),
    fontWeight: z.number().int().min(300).max(900).multipleOf(100).optional(),
    lineHeightPx: z.number().int().min(12).max(80).optional(),
    letterSpacingPx: z.number().min(-2).max(8).optional(),
    italic: z.boolean().optional(),
    uppercase: z.boolean().optional(),
    color: chartHexColorSchema.optional(),
    textAlign: z.enum(REPORT_TEXT_ALIGNS).optional(),
  })
  .strict();

export type ReportTextStyle = z.infer<typeof reportTextStyleSchema>;

/** Bounded container padding and gap between stacked children, in pixels. */
export const reportSpacingSchema = z
  .object({
    paddingXPx: reportSpacingPxSchema.optional(),
    paddingYPx: reportSpacingPxSchema.optional(),
    gapPx: reportSpacingPxSchema.optional(),
  })
  .strict();

export type ReportSpacing = z.infer<typeof reportSpacingSchema>;

export const REPORT_SECTION_LABEL_TREATMENTS = [
  "bar",
  "underline",
  "plain",
] as const;

export const reportSectionPresentationSchema = z
  .object({
    backgroundColor: z.string().nullable().optional(),
    labelTreatment: z
      .enum(REPORT_SECTION_LABEL_TREATMENTS)
      .nullable()
      .optional(),
    labelStyle: reportTextStyleSchema.nullable().optional(),
    headlineStyle: reportTextStyleSchema.nullable().optional(),
    bodyStyle: reportTextStyleSchema.nullable().optional(),
    spacing: reportSpacingSchema.nullable().optional(),
  })
  .strict();

export const REPORT_HIGHLIGHT_MAX_ACCENT_WIDTH_PX = 12;

export const reportHighlightPresentationSchema = z
  .object({
    borderColor: z.string().nullable().optional(),
    backgroundColor: z.string().nullable().optional(),
    accentWidthPx: z
      .number()
      .int()
      .min(0)
      .max(REPORT_HIGHLIGHT_MAX_ACCENT_WIDTH_PX)
      .nullable()
      .optional(),
    titleStyle: reportTextStyleSchema.nullable().optional(),
    contentStyle: reportTextStyleSchema.nullable().optional(),
    spacing: reportSpacingSchema.nullable().optional(),
  })
  .strict();

export const reportComponentThemeSchema = z
  .object({
    reportSection: reportSectionPresentationSchema.optional(),
    reportHighlight: reportHighlightPresentationSchema.optional(),
  })
  .strict();

export type ReportComponentTheme = z.infer<typeof reportComponentThemeSchema>;
export type ReportSectionPresentation = z.infer<
  typeof reportSectionPresentationSchema
>;
export type ReportHighlightPresentation = z.infer<
  typeof reportHighlightPresentationSchema
>;

/** Null and omitted values inherit; explicit false and zero override defaults. */
const mergeReportStyle = <TStyle extends ReportTextStyle | ReportSpacing>(
  defaults: TStyle | null | undefined,
  overrides: TStyle | null | undefined,
): TStyle =>
  ({
    ...defaults,
    ...Object.fromEntries(
      Object.entries(overrides ?? {}).filter(([, value]) => value != null),
    ),
  }) as TStyle;

export const resolveReportSectionPresentation = (
  defaults: ReportSectionPresentation | undefined,
  overrides: ReportSectionPresentation,
): ReportSectionPresentation => ({
  backgroundColor: overrides.backgroundColor ?? defaults?.backgroundColor,
  labelTreatment: overrides.labelTreatment ?? defaults?.labelTreatment,
  labelStyle: mergeReportStyle(defaults?.labelStyle, overrides.labelStyle),
  headlineStyle: mergeReportStyle(
    defaults?.headlineStyle,
    overrides.headlineStyle,
  ),
  bodyStyle: mergeReportStyle(defaults?.bodyStyle, overrides.bodyStyle),
  spacing: mergeReportStyle(defaults?.spacing, overrides.spacing),
});

export const resolveReportHighlightPresentation = (
  defaults: ReportHighlightPresentation | undefined,
  overrides: ReportHighlightPresentation,
): ReportHighlightPresentation => ({
  borderColor: overrides.borderColor ?? defaults?.borderColor,
  backgroundColor: overrides.backgroundColor ?? defaults?.backgroundColor,
  accentWidthPx: overrides.accentWidthPx ?? defaults?.accentWidthPx,
  titleStyle: mergeReportStyle(defaults?.titleStyle, overrides.titleStyle),
  contentStyle: mergeReportStyle(
    defaults?.contentStyle,
    overrides.contentStyle,
  ),
  spacing: mergeReportStyle(defaults?.spacing, overrides.spacing),
});

/** Catalog description fragment for props typed by {@link reportTextStyleSchema}. */
export const REPORT_TEXT_STYLE_DESCRIPTION =
  "fontFamily serif|sans|mono (omit to inherit the theme font), fontSizePx 10-64, fontWeight 300-900 in steps of 100, lineHeightPx 12-80, letterSpacingPx -2 to 8, italic, uppercase, color as #RRGGBB hex, textAlign left|center|right";

/** Catalog description fragment for props typed by {@link reportSpacingSchema}. */
export const REPORT_SPACING_DESCRIPTION =
  "paddingXPx, paddingYPx, gapPx (integers 0-64)";

/** Returns a persisted text style only when it passes {@link reportTextStyleSchema}. */
export const parseReportTextStyle = (
  value: unknown,
): ReportTextStyle | undefined => {
  const result = reportTextStyleSchema.safeParse(value);
  return result.success ? result.data : undefined;
};

/** Returns persisted spacing only when it passes {@link reportSpacingSchema}. */
export const parseReportSpacing = (
  value: unknown,
): ReportSpacing | undefined => {
  const result = reportSpacingSchema.safeParse(value);
  return result.success ? result.data : undefined;
};

/** Maps a saved {@link ReportTextStyle} to inline styles; omitted keys keep component defaults. */
export const resolveReportTextStyle = (
  textStyle: ReportTextStyle | null | undefined,
): CSSProperties => {
  if (textStyle == null) return {};

  return {
    ...(textStyle.fontFamily != null && {
      fontFamily: REPORT_FONT_FAMILY_STACKS[textStyle.fontFamily],
    }),
    ...(textStyle.fontSizePx != null && { fontSize: textStyle.fontSizePx }),
    ...(textStyle.fontWeight != null && { fontWeight: textStyle.fontWeight }),
    ...(textStyle.lineHeightPx != null && {
      lineHeight: `${textStyle.lineHeightPx}px`,
    }),
    ...(textStyle.letterSpacingPx != null && {
      letterSpacing: textStyle.letterSpacingPx,
    }),
    ...(textStyle.italic != null && {
      fontStyle: textStyle.italic ? "italic" : "normal",
    }),
    ...(textStyle.uppercase != null && {
      textTransform: textStyle.uppercase ? "uppercase" : "none",
    }),
    ...(textStyle.color != null && { color: textStyle.color }),
    ...(textStyle.textAlign != null && { textAlign: textStyle.textAlign }),
  };
};

/** Maps a saved {@link ReportSpacing} to inline padding and flex gap. */
export const resolveReportSpacingStyle = (
  spacing: ReportSpacing | null | undefined,
): CSSProperties => {
  if (spacing == null) return {};

  return {
    ...(spacing.paddingXPx != null && {
      paddingLeft: spacing.paddingXPx,
      paddingRight: spacing.paddingXPx,
    }),
    ...(spacing.paddingYPx != null && {
      paddingTop: spacing.paddingYPx,
      paddingBottom: spacing.paddingYPx,
    }),
    ...(spacing.gapPx != null && { gap: spacing.gapPx }),
  };
};
